/**
 * Types and constants for PO Lines Tab components
 */
import { PurchaseOrderLine } from '../../../../lib/api';

// CR-3406: Column configuration for visibility management
// CR-3846: Added order field for column reordering
export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  minWidth: string;
  order: number;  // CR-3846: Column display order
}

// CR-3850: Added additional columns from old platform
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'acknowledge_status', label: 'Ack Status', visible: true, sortable: true, minWidth: '170px', order: 0 },
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '100px', order: 1 },
  { id: 'is_critical', label: 'Critical', visible: true, sortable: true, minWidth: '80px', order: 2 },
  { id: 'on_hold', label: 'On Hold', visible: true, sortable: true, minWidth: '100px', order: 3 },
  { id: 'hold_reason', label: 'Hold Reason', visible: true, sortable: false, minWidth: '120px', order: 4 },
  { id: 'next_action', label: 'Next Action', visible: true, sortable: true, minWidth: '120px', order: 5 },
  // CR-6510: procurement traceability — whether a deviation has been raised
  // against the line and where it stands. Off by default: it is empty for
  // almost every line, and customers without concessions enabled would get a
  // permanently blank column.
  { id: 'concession_status', label: 'Concession', visible: false, sortable: false, minWidth: '150px', order: 5.5 },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, minWidth: '140px', order: 6 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 7 },
  { id: 'erp_order_type', label: 'ERP Order Type', visible: true, sortable: false, minWidth: '120px', order: 7.5 },
  { id: 'customer_name', label: 'Customer', visible: true, sortable: true, minWidth: '150px', order: 8 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, minWidth: '220px', order: 9 },
  { id: 'supplier_group_name', label: 'Supplier Group', visible: false, sortable: true, minWidth: '150px', order: 10 },  // CR-3850
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '160px', order: 12 },
  { id: 'description', label: 'Description', visible: true, sortable: true, minWidth: '200px', order: 13 },
  { id: 'quantity', label: 'Qty Ordered', visible: true, sortable: true, minWidth: '140px', order: 14 },
  { id: 'received_qty', label: 'Qty Delivered', visible: true, sortable: true, minWidth: '160px', order: 15 },
  // Per-PO net warehouse stock allocated to this line (StockPOAllocation
  // ledger). Distinct from received_qty, which is what the customer has
  // GRN'd. Not sortable: it reads from an annotation, not a model field.
  { id: 'delivered_warehouse_qty', label: 'Delivered into Warehouse', visible: true, sortable: false, minWidth: '200px', order: 16 },
  // CR-7163 (a): supplier-side Fulfilment %, driven by supplier warehouse
  // stock — delivered_warehouse_qty / outstanding_qty. Sits immediately after
  // the qty it derives from so the pair reads together. Fractional order
  // deliberately: 16-21 are taken, and renumbering them would interleave
  // oddly for users whose saved column order is merged by id from
  // localStorage. Not sortable — derived client-side, not a model field.
  { id: 'warehouse_fulfilment_pct', label: 'Fulfilment %', visible: true, sortable: false, minWidth: '150px', order: 16.5 },
  { id: 'outstanding_qty', label: 'Qty O/S', visible: true, sortable: true, minWidth: '140px', order: 17 },
  { id: 'pct_delivered', label: '% Delivered', visible: true, sortable: true, minWidth: '130px', order: 18 },
  { id: 'unit_price', label: 'Unit Price', visible: true, sortable: true, minWidth: '140px', order: 19 },
  { id: 'total_price', label: 'Total', visible: true, sortable: true, minWidth: '110px', order: 20 },
  { id: 'currency', label: 'Currency', visible: false, sortable: true, minWidth: '100px', order: 21 },
  // CR-4694 round 2 rework: 'Customer Required Date' (delivery_date) column
  // removed from the table view per spec — the field was being populated by
  // the import path that should have been writing to MRP/ERP Scheduled, so
  // its values were misleading. The delivery_date model field is unchanged
  // (OTIF, on-time, and many other downstream calculations still depend on
  // it), only the surface in the column picker is removed.
  { id: 'supplier_promise_date', label: 'Latest Supplier Promise Date', visible: true, sortable: true, minWidth: '220px', order: 22 },
  { id: 'buyer_id', label: 'Buyer ID', visible: true, sortable: true, minWidth: '100px', order: 23 },
  { id: 'batch_serial_no', label: 'Batch/Serial No', visible: false, sortable: true, minWidth: '140px', order: 24 },
  { id: 'ppv', label: 'PPV', visible: false, sortable: true, minWidth: '100px', order: 25 },
  { id: 'otif_overridden', label: 'OTIF Overridden', visible: false, sortable: true, minWidth: '130px', order: 26 },
  { id: 'packing_note_number', label: 'PnR Note No', visible: false, sortable: true, minWidth: '120px', order: 27 },
  { id: 'order_date', label: 'Order Date', visible: false, sortable: true, minWidth: '120px', order: 28 },
  { id: 'last_received_date', label: 'Last Received', visible: false, sortable: true, minWidth: '120px', order: 29 },
  { id: 'updated_at', label: 'Update Time', visible: false, sortable: true, minWidth: '150px', order: 30 },
  { id: 'required_date_change_count', label: 'Req Date Changes', visible: false, sortable: true, minWidth: '130px', order: 31 },
  { id: 'promised_date_change_count', label: 'Promise Date Changes', visible: false, sortable: true, minWidth: '150px', order: 32 },
  { id: 'pdf_attached_count', label: 'PDF Attached', visible: false, sortable: true, minWidth: '100px', order: 33 },
  { id: 'material_cert_count', label: 'Mat Certs', visible: false, sortable: true, minWidth: '100px', order: 34 },
  { id: 'contract_unit_price', label: 'Contract Unit Price', visible: false, sortable: true, minWidth: '140px', order: 35 },
  { id: 'contract_lot_price', label: 'Contract Lot Price', visible: false, sortable: true, minWidth: '140px', order: 36 },
  { id: 'contract_price', label: 'Contract Price', visible: false, sortable: true, minWidth: '130px', order: 37 },
  { id: 'drawing_number', label: 'Drawing No', visible: false, sortable: true, minWidth: '120px', order: 38 },
  { id: 'drawing_version', label: 'Drawing Issue', visible: false, sortable: true, minWidth: '100px', order: 39 },
  { id: 'outstanding_value', label: 'O/S Value', visible: false, sortable: true, minWidth: '120px', order: 40 },
  { id: 'converted_unit_price', label: 'Conv. Unit Price', visible: false, sortable: true, minWidth: '130px', order: 41 },
  { id: 'converted_total_value', label: 'Conv. Total', visible: false, sortable: true, minWidth: '120px', order: 42 },
  { id: 'converted_outstanding_value', label: 'Conv. O/S Value', visible: false, sortable: true, minWidth: '130px', order: 43 },
  // CR-5548 round 3: the "Agreed Date (ODD)" column previously bound to
  // PurchaseOrderLine.original_promise_date, an immutable snapshot of the
  // first agreed date kept "for tracking changes" (per the model field
  // help_text). Rounds 1 (commit d430d5dc) and 2 (commit ba5bb674) wrote
  // the post-accept date into delivery_date / schedule_date, so the
  // backend numbers were correct, but the UI column was reading the
  // wrong field — it kept showing the original promise even after a
  // change was accepted. Bind the column to delivery_date instead so it
  // reflects the currently-agreed delivery date the spec asks for.
  // The 'original_promise_date' field is still serialized for any caller
  // that genuinely needs the original snapshot (audit / scorecard
  // pre-accept lookups); we just stop using it as the user-facing
  // "Agreed Date" column source.
  // Customer review 11 Sep: the column now shows the server's odd_date — the
  // ODD the verdict, OTIF and arrears measure against (original_promise_date,
  // else delivery_date, at PO-line grain; the slot date at schedule grain).
  // An accepted change moves delivery_date but not that ODD, so the column
  // follows the metric, not the latest agreement. The id stays
  // 'delivery_date' so saved column layouts keep working.
  { id: 'delivery_date', label: 'Agreed Date (ODD)', visible: false, sortable: true, minWidth: '140px', order: 44 },
  // CR-4694 round 2 rework: MRP/ERP Scheduled now visible by default
  // since it replaces the removed Customer Required Date column as the
  // primary import-driven date surface.
  { id: 'mrp_scheduled_date', label: 'MRP/ERP Scheduled', visible: true, sortable: true, minWidth: '150px', order: 45 },
  { id: 'priority', label: 'Priority', visible: false, sortable: true, minWidth: '100px', order: 46 },
  { id: 'exchange_rate', label: 'Exchange Rate', visible: false, sortable: true, minWidth: '120px', order: 47 },  // CR-4679
];

export const COLUMN_STORAGE_KEY = 'po_lines_column_config';

// CR-4716: Commercial/internal fields restricted to customer users only.
// Supplier users must not see these in the table, column picker, or filters.
// Converted/exchange-rate fields are the customer's reporting-currency view —
// suppliers must only see the original transaction-currency price.
/**
 * CR-7163 (a): columns only the supplier sees. The mirror of
 * SUPPLIER_RESTRICTED_COLUMNS — those are hidden FROM suppliers, these are
 * hidden from everyone else.
 *
 * Fulfilment % answers "how much of what I still owe is sitting in my
 * warehouse ready to ship" — a supplier production question. The customer
 * already has "Delivered into Warehouse" (the qty) and "% Delivered" (their
 * own receipts), so this would only duplicate their view.
 */
export const SUPPLIER_ONLY_COLUMNS = [
  'warehouse_fulfilment_pct',
];

export const SUPPLIER_RESTRICTED_COLUMNS = [
  'buyer_id',
  'ppv',
  'otif_overridden',
  'contract_unit_price',
  'contract_lot_price',
  'contract_price',
  'converted_unit_price',
  'converted_total_value',
  'converted_outstanding_value',
  'exchange_rate',
];

// Filter state interface
// CR-3413: Removed dateFrom/dateTo (Delivery From/To), combined status filters
export interface POLinesFilters {
  poNumber: string;
  lineNumber: string;
  supplier: string;
  supplierSearch: string;  // CR-3413: For searchable supplier dropdown
  supplierGroup: string;
  customer: string;
  customerSearch: string;  // CR-3413: For searchable customer/site dropdown
  partNumber: string;
  description: string;
  erpOrderType: string;
  status: string;  // CR-3413: Combined status filter (replaces separate status and acknowledgeStatus)
  oddFrom: string;
  oddTo: string;
  on_hold: string;
  buyerId: string;
  batchSerialNo: string;
  otifOverridden: string;
  nextAction: string;
  ppv: string;
  commodityGroup: string;
  commodityType: string;
  // CR-6348: reworked SO Lines filter panel — Company/Supplier hierarchy +
  // legacy status filters.
  companyCountry: string;
  division: string;
  warehouse: string;
  supplierCountry: string;
  supplierCode: string;  // CR-7028: Supplier code filter
  oddStatus: string;
  otdStatus: string;
  criticalOnly: string;  // '' | 'true' — Critical lines only checkbox
}

// CR-3413: Removed dateFrom/dateTo, acknowledgeStatus (now combined into status)
export const DEFAULT_FILTERS: POLinesFilters = {
  poNumber: '',
  lineNumber: '',
  supplier: '',
  supplierSearch: '',
  supplierGroup: '',
  customer: '',
  customerSearch: '',
  partNumber: '',
  description: '',
  erpOrderType: '',
  status: '',
  oddFrom: '',
  oddTo: '',
  on_hold: '',
  buyerId: '',
  batchSerialNo: '',
  otifOverridden: '',
  nextAction: '',
  ppv: '',
  commodityGroup: '',
  commodityType: '',
  // CR-6348
  companyCountry: '',
  division: '',
  warehouse: '',
  supplierCountry: '',
  supplierCode: '',  // CR-7028
  oddStatus: '',
  otdStatus: '',
  criticalOnly: '',
};

// CR-3413: Unified status options combining Status and Acknowledge Status
// Note: 'changed' maps to change_proposed_supplier + change_proposed_customer in the backend
// Note: 'on_hold' maps to the on_hold boolean flag (not an acknowledge_status)
// Note: 'closed' maps to PO line status 'received'
// Raw ERP order-type codes (DE: 4 = scheduling agreement, 1 = standard/spot).
export const ERP_ORDER_TYPE_OPTIONS = [
  { value: '4', label: 'Scheduling Agreement (4)' },
  { value: '1', label: 'Standard / Spot (1)' },
  { value: '2', label: 'Type 2' },
  { value: '3', label: 'Type 3' },
];

export const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'changed', label: 'Changed' },
  { value: 'change_declined', label: 'Change Declined' },
  { value: 'wip', label: 'WIP' },
  { value: 'declined', label: 'Declined' },
  { value: 'despatched', label: 'Dispatched' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

// Filter options from API
export interface FilterOptions {
  suppliers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  customers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  commodity_groups: Array<{ id: number; name: string }>;
  commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
  supplier_groups?: Array<{ id: number; name: string }>;
  // CR-6348: option-lists for the reworked SO Lines filter panel.
  divisions?: Array<{ id: number; name: string }>;
  warehouses?: Array<{ id: number; name: string; site: number }>;
  site_countries?: string[];
  supplier_countries?: string[];
}

// CR-6348: "ODD Status" maps to the PO line's stored otif_status column
// (adherence to the agreed / Original Delivery Date).
export const ODD_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending – Not Yet Due' },
  { value: 'otif', label: 'OTIF – On Time In Full' },
  { value: 'early', label: 'Early Delivery' },
  { value: 'late', label: 'Late Delivery' },
  { value: 'short', label: 'Short Delivery' },
  { value: 'arrear', label: 'Arrear – Overdue & Not Received' },
];

// CR-6348: "OTD Status to LSPD" — on-time delivery measured against the Latest
// Supplier Promise Date (backend annotates last receipt vs supplier_promise_date).
export const OTD_LSPD_STATUS_OPTIONS = [
  { value: 'on_time', label: 'On Time' },
  { value: 'late', label: 'Late' },
  { value: 'not_received', label: 'Not Received' },
];

// Acknowledge status configuration
export interface StatusConfig {
  label: string;
  className: string;
}

export const ACKNOWLEDGE_STATUS_CONFIG: Record<string, StatusConfig> = {
  new: { label: 'New', className: 'bg-blue-500 text-white' },
  declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
  change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
  change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
  wip: { label: 'WIP', className: 'bg-green-500 text-white' },
  accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
  despatched: { label: 'Dispatched', className: 'bg-gray-200 text-gray-900' },
  change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  closed: { label: 'Closed', className: 'bg-gray-400 text-white' },
};

// Next action result type
export interface NextActionResult {
  text: string;
  className: string;
}

// Re-export PurchaseOrderLine for convenience
export type { PurchaseOrderLine };
